<template>
  <calm-page extra-class="usr" :title="$t('menu.user')">
    <template slot="sub">
      <span v-if="Version" class="c-mono c-mute">{{ Version }}</span>
    </template>
    <template slot="actions">
      <calm-button variant="primary" @click="handleCreate()">
        <i class="fas fa-plus mr-1"></i> {{ $t("new") }}
      </calm-button>
    </template>

    <!-- ═══════════════ SEARCH BAR ═══════════════ -->
    <calm-filter-bar :hide-actions="true">
      <calm-field :label="$t('search')">
        <input
          v-model="searchQuery"
          type="text"
          class="ed"
          :placeholder="$t('search')"
        />
      </calm-field>
    </calm-filter-bar>

    <!-- ═══════════════ TABLE (skin the existing el-table verbatim, §11) ═══════════════ -->
    <calm-table
      :total="total"
      :from="from"
      :to="to"
      :per-page.sync="pagination.perPage"
      :current-page.sync="pagination.currentPage"
      :per-page-options="pagination.perPageOptions"
      :per-page-label="$t('per_page') !== 'per_page' ? $t('per_page') : 'ต่อหน้า'"
      :count-label="$t('data')"
      :to-label="$t('to')"
      :of-label="$t('of')"
      :empty-title="$t('no_data') !== 'no_data' ? $t('no_data') : 'ไม่พบข้อมูล'"
    >
      <el-table
        :data="queriedData"
        row-key="id"
        header-row-class-name="thead-light"
        @sort-change="sortChange"
        @selection-change="selectionChange"
      >
        <!-- avatar cell (read-only, small) -->
        <el-table-column align="center" width="64px">
          <div slot-scope="{ row }" class="usr__avatar-cell">
            <calm-avatar-upload
              :editable="false"
              size="sm"
              :value="avatarCache[row.PersonID] || ''"
              :name="(row.FName || '') + ' ' + (row.LName || '')"
            />
          </div>
        </el-table-column>

        <el-table-column
          v-for="column in tableColumns"
          :key="column.label"
          v-bind="column"
        >
        </el-table-column>
        <el-table-column align="left" :label="$t('status')">
          <div slot-scope="{ $index, row }" class="d-flex">
            <span
              class="calm__pill"
              :class="row.IsActive == 1 ? 'calm__pill--success' : 'calm__pill--neutral'"
            >
              <template v-if="row.IsActive == 1">{{ $t("status_active") }}</template>
              <template v-else>{{ $t("status_not_active") }}</template>
            </span>
          </div>
        </el-table-column>

        <el-table-column
          min-width="280px"
          align="right"
          :label="$t('actions')"
        >
          <div slot-scope="{ $index, row }" class="usr__rowact">
            <calm-button
              variant="ghost"
              size="sm"
              @click.native="handleEdit(row)"
            >
              <i class="fas fa-pen mr-1"></i>{{ $t("edit") }}
            </calm-button>

            <calm-button
              variant="ghost"
              size="sm"
              @click.native="handleChangePassword(row)"
            >
              <i class="fas fa-key mr-1"></i>{{ $t("profile.change_password") }}
            </calm-button>

            <calm-button
              variant="ghost"
              size="sm"
              @click.native="handleSetActive(row)"
            >
              <i class="fas fa-toggle-on mr-1"></i>{{ $t("status") }}
            </calm-button>
          </div>
        </el-table-column>
      </el-table>
    </calm-table>

    <!-- ------------------------ Modal Create|Password|Status ------------------------ -->
    <calm-modal
      :show.sync="showAddModal"
      :show-footer="false"
      :title="
        md.Form == 1
          ? $t('profile.create')
          : md.Form == 2
          ? $t('profile.change_password')
          : $t('profile.change_status')
      "
    >
      <validation-observer v-slot="{ handleSubmit }" ref="formValidator">
        <form
          class="usr__form"
          @submit.prevent="handleSubmit(onUpdateUser)"
          v-if="md.Form == 1"
        >
          <base-input
            :label="$t('profile.email')"
            v-model="md.Username"
            rules="required|email"
            :name="$t('profile.email')"
          >
          </base-input>
          <base-input
            :label="$t('profile.password')"
            v-model="md.Password"
            rules="required|min:6|max:15"
            :name="$t('profile.password')"
          >
          </base-input>

          <base-input
            :label="$t('profile.type')"
            input-classes="form-control-alternative new-event--title"
            rules="required"
            :name="$t('profile.type')"
          >
            <el-select
              v-model="md.Type"
              popper-class="calm-pop"
              :placeholder="$t('profile.type')"
            >
              <el-option
                v-for="option in userType"
                :key="option.ID"
                :label="option.Name"
                :value="option.ID"
              >
              </el-option>
            </el-select>
          </base-input>
          <base-input
            :label="$t('lmg.person')"
            input-classes="form-control-alternative new-event--title"
            rules="required"
            :name="$t('lmg.person')"
          >
            <el-select
              v-model="md.PersonID"
              popper-class="calm-pop"
              :placeholder="$t('lmg.person')"
            >
              <el-option
                v-for="option in tablePersonNoLogin"
                :key="option.ID"
                :label="option.Name"
                :value="option.ID"
              >
              </el-option>
            </el-select>
          </base-input>

          <div class="calm__modal__actions">
            <calm-button variant="ghost" @click="showAddModal = false">
              {{ $t("close") }}
            </calm-button>
            <calm-button variant="primary" native-type="submit">
              {{ $t("save") }}
            </calm-button>
          </div>
        </form></validation-observer
      >

      <!-- ------------------------ Modal Change Password ------------------------ -->
      <validation-observer v-slot="{ handleSubmit }" ref="formValidatorPassword">
        <form
          v-if="md.Form == 2"
          class="usr__form"
          @submit.prevent="handleSubmit(onUpdatePassword)"
        >
          <base-input :label="$t('profile.email')" :value="md.Username">
          </base-input>
          <base-input
            :label="$t('profile.password')"
            v-model="md.Password"
            rules="required|min:6|max:15"
            :name="$t('profile.password')"
          >
          </base-input>

          <div class="calm__modal__actions">
            <calm-button variant="ghost" @click="showAddModal = false">
              {{ $t("close") }}
            </calm-button>
            <calm-button variant="primary" native-type="submit">
              {{ $t("save") }}
            </calm-button>
          </div>
        </form>
      </validation-observer>

      <!-- ------------------------ Modal Change Status ------------------------ -->
      <validation-observer v-slot="{ handleSubmit }" ref="formValidatorStatus">
        <form
          v-if="md.Form == 3"
          class="usr__form"
          @submit.prevent="handleSubmit(onUpdateStatus)"
        >
          <base-input
            :label="$t('status')"
            input-classes="form-control-alternative new-event--title"
            v-if="md.Form == 3"
          >
            <el-select
              v-model="md.IsActive"
              popper-class="calm-pop"
              :placeholder="$t('status')"
            >
              <el-option
                v-for="option in activeStatus"
                :key="option.ID"
                :label="option.Name"
                :value="option.ID"
              >
              </el-option>
            </el-select>
          </base-input>

          <div class="calm__modal__actions">
            <calm-button variant="ghost" @click="showAddModal = false">
              {{ $t("close") }}
            </calm-button>
            <calm-button variant="primary" native-type="submit">
              {{ $t("save") }}
            </calm-button>
          </div>
        </form>
      </validation-observer>
    </calm-modal>

    <!-- ------------------------ Modal Edit User (linked person) ------------------------ -->
    <modal
      :show.sync="showEditModal"
      modal-classes="modal-secondary modal-lg"
      custom-class="calm__dialog calm"
    >
      <validation-observer v-slot="{ handleSubmit }" ref="formValidatorEdit">
        <form
          class="usr__form usr__edit"
          @submit.prevent="handleSubmit(onUpdatePersonFromUser)"
        >
          <h1>{{ $t("edit") }}</h1>

          <!-- ─────── Avatar ─────── -->
          <div class="usr__edit-avatar">
            <calm-avatar-upload
              v-model="image"
              size="lg"
              editable
              :name="md.FName + ' ' + md.LName"
              @change="onPhotoChange"
              @remove="onPhotoRemove"
            />
          </div>

          <!-- ─────── 1. ข้อมูลทั่วไป (read-only role) ─────── -->
          <calm-form-section :num="1" :title="$t('profile.type')">
            <div class="calm__form-grid calm__form-grid--2">
              <calm-form-row :label="$t('profile.email')">
                <base-input :name="$t('profile.email')" :value="md.Username" disabled></base-input>
              </calm-form-row>
              <calm-form-row :label="$t('profile.type')">
                <base-input :name="$t('profile.type')" :value="md.UserTypeDesc" disabled></base-input>
              </calm-form-row>
            </div>
          </calm-form-section>

          <!-- ─────── 2. ข้อมูลส่วนตัว (Person) ─────── -->
          <calm-form-section :num="2" :title="$t('name')">
            <div class="calm__form-grid calm__form-grid--3">
              <calm-form-row :label="$t('fname')" required>
                <base-input
                  :name="$t('fname')"
                  rules="required"
                  v-model="md.FName"
                ></base-input>
              </calm-form-row>
              <calm-form-row :label="$t('lname')" required>
                <base-input
                  :name="$t('lname')"
                  rules="required"
                  v-model="md.LName"
                ></base-input>
              </calm-form-row>
              <calm-form-row :label="$t('lmg.tel')">
                <base-input :name="$t('lmg.tel')" v-model="md.Tel"></base-input>
              </calm-form-row>
              <calm-form-row :label="$t('lmg.email')">
                <base-input
                  :name="$t('lmg.email')"
                  rules="email"
                  v-model="md.Email"
                ></base-input>
              </calm-form-row>
            </div>
          </calm-form-section>

          <!-- ─────── 3. ที่อยู่ (Address) ─────── -->
          <calm-form-section :num="3" :title="$t('lmg.address')">
            <div class="calm__form-grid calm__form-grid--3">
              <calm-form-row :label="$t('lmg.address')">
                <base-input :name="$t('lmg.address')" v-model="md.Address"></base-input>
              </calm-form-row>
              <calm-form-row :label="$t('lmg.subdistrict')">
                <base-input :name="$t('lmg.subdistrict')" v-model="md.Subdistrict"></base-input>
              </calm-form-row>
              <calm-form-row :label="$t('lmg.city')">
                <base-input :name="$t('lmg.city')" v-model="md.City"></base-input>
              </calm-form-row>
              <calm-form-row :label="$t('lmg.province')">
                <base-input :name="$t('lmg.province')" v-model="md.Province"></base-input>
              </calm-form-row>
              <calm-form-row :label="$t('lmg.postcode')">
                <base-input
                  :name="$t('lmg.postcode')"
                  rules="digits:5"
                  v-model="md.Postcode"
                ></base-input>
              </calm-form-row>
            </div>
          </calm-form-section>

          <!-- ─────── 4. สาขา (Branch) ─────── -->
          <calm-form-section :num="4" :title="$t('lmg.branch')">
            <div class="calm__form-grid calm__form-grid--2">
              <calm-form-row :label="$t('lmg.branch')" v-if="isSuperAdmin">
                <base-input :label="$t('lmg.branch')">
                  <el-select
                    v-model="md.BranchID"
                    popper-class="calm-pop"
                    :placeholder="$t('lmg.branch')"
                  >
                    <el-option
                      v-for="option in tableBranch"
                      :key="option.ID"
                      :label="option.Name"
                      :value="option.ID"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </calm-form-row>
              <calm-form-row :label="$t('lmg.branch')" v-else>
                <base-input :name="$t('lmg.branch')" :value="md.BranchName" disabled></base-input>
              </calm-form-row>
            </div>
          </calm-form-section>

          <div class="calm__modal__actions usr__actions">
            <calm-button variant="ghost" @click="showEditModal = false">
              {{ $t("close") }}
            </calm-button>
            <calm-button variant="primary" native-type="submit">
              {{ $t("save") }}
            </calm-button>
          </div>
        </form>
      </validation-observer>
    </modal>
  </calm-page>
</template>
<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import clientPaginationMixin from "./PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import CalmAvatarUpload from "@/components/lmg/CalmAvatarUpload";

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    RouteBreadCrumb,
    CalmAvatarUpload,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  computed: {
    ...mapGetters({
      tableDataOrg: "User/getData",
      tablePersonNoLogin: "Person/getDataNoLogin",
      tablePersonFull: "Person/getData",
      tableBranch: "Branch/getData"
    }),
    tableData: function() {
      return this.tableDataOrg;
    },
    isSuperAdmin() {
      // role 99 == super admin; profile is persisted in auth module
      let role = this.$store.getters["Auth/getUser"]
        ? this.$store.getters["Auth/getUser"].Type
        : null;
      return role == 99;
    },
    tableColumns: function() {
      return [
        {
          prop: "UserName",
          label: this.$t("profile.email"),
          minWidth: 90,
          sortable: true
        },
        {
          prop: "FName",
          label: this.$t("profile.name"),
          minWidth: 100,
          sortable: true
        },
        {
          prop: "LName",
          label: this.$t("profile.lname"),
          minWidth: 120,
          sortable: true
        },
        {
          prop: "UserTypeDesc",
          label: this.$t("profile.type"),
          minWidth: 120,
          sortable: true
        },
        {
          prop: "Tel",
          label: this.$t("profile.phone"),
          minWidth: 90,
          sortable: true
        }
      ];
    }
  },
  data() {
    return {
      propsToSearch: ["Number", "Email"],
      selectedRows: [],
      showAddModal: false,
      showEditModal: false,
      image: "",
      avatarCache: {},
      Version: "",
      userType: [
        { ID: 2, Name: "User" },
        { ID: 3, Name: "Branch Admin" }
      ],
      md: {
        Form: 0,
        ID: 0,
        Username: "",
        Password: "",
        Type: 2,
        PersonID: "",
        IsActive: 1,
        // --- linked-person edit fields (round-tripped to avoid corruption) ---
        UserTypeDesc: "",
        FName: "",
        LName: "",
        BirthDay: "",
        AgentCode: "",
        WithholdingTax: "",
        TaxID: "",
        Tel: "",
        Email: "",
        Address: "",
        Subdistrict: "",
        City: "",
        Province: "",
        Postcode: "",
        UpperID: 0,
        UserTypeID: 0,
        ComprofileID: 0,
        BranchID: 0,
        BranchName: "",
        PersonIsActive: 1,
        PhotoFile: null
      },
      activeStatus: [
        {
          ID: 1,
          Name: "ใช้งาน"
        },
        {
          ID: 0,
          Name: "ไม่ใช้งาน"
        }
      ]
    };
  },
  async mounted() {
    await this.listData();
    await this.listPerson();
    await this.listPersonFull();
    await this.listBranch();

    this.GetApiVersion()
      .then(resp => {
        this.Version =
          "API V." +
          resp.data.Data[0].APIVersion +
          "  Core V." +
          resp.data.Data[0].CoreVersion;
      })
      .catch(err => {});

    this.loadRowAvatars();
  },
  methods: {
    ...mapActions({
      createData: "User/createData",
      updateData: "User/updateData",
      deleteData: "User/deleteData",
      listData: "User/listData",
      listPerson: "Person/listDataNoLogin",
      listPersonFull: "Person/listData",
      updatePerson: "Person/updateData",
      getImage: "Person/getImage",
      listBranch: "Branch/listData",
      changePasswordByAdmin: "User/changePasswordByAdmin",
      setActive: "User/setActive",
      GetApiVersion: "User/apiVersion"
    }),
    // Lazily fetch base64 avatars only for rows that actually have a photo.
    loadRowAvatars() {
      (this.tableData || []).forEach(row => {
        if (
          row.Photo &&
          row.Photo != "" &&
          row.PersonID &&
          this.avatarCache[row.PersonID] === undefined
        ) {
          this.getImage(row.PersonID)
            .then(resp => {
              this.$set(this.avatarCache, row.PersonID, resp.data);
            })
            .catch(() => {
              this.$set(this.avatarCache, row.PersonID, "");
            });
        }
      });
    },
    clearModal(force) {
      if (this.md.ID == 0 || force) return;
      this.md.ID = 0;
      this.md.Username = "";
      this.md.Password = "";
      this.md.IsActive = "";
      this.md.Type = 2;
      this.md.PersonID = this.tablePersonNoLogin[0].ID;
    },
    handleCreate() {
      this.$refs.formValidator.reset();
      this.showAddModal = true;
      this.md.Form = 1;
      this.clearModal(false);
    },
    // Open the linked-person edit modal, prefilled from the FULL person record.
    handleEdit(row) {
      this.md.Form = 4;
      this.image = "";
      this.md.PhotoFile = null;
      this.md.RemovePhoto = false;

      // Look up the full person record (ResPersonData) for the round-trip.
      // PersonUpdateV2 is a FULL-RECORD overwrite, so we can only edit a person
      // whose complete record we hold. The prefill source (PersonListV2) is
      // FILTERED (branch-admin: is_active=1 AND type_id!=99) while UserList is
      // not — so some user rows have no matching full record. Submitting from
      // partial data would zero comprofile_id/type_id/upper_person_id and
      // reactivate the row, corrupting a real record. Abort instead of guessing.
      let p = (this.tablePersonFull || []).find(x => x.ID == row.PersonID);
      if (!p) {
        swal.fire({
          title: this.$t("errors.invalid_parameter"),
          text: this.$t("user.edit_unavailable"),
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false
        });
        return;
      }

      // user-level (display only)
      this.md.ID = row.ID;
      this.md.Username = row.UserName;
      this.md.UserTypeDesc = row.UserTypeDesc;
      this.md.PersonID = row.PersonID;

      this.md.FName = p.FName;
      this.md.LName = p.LName;
      this.md.BirthDay = p.BirthDay == "0000-00-00" ? "" : p.BirthDay;
      this.md.AgentCode = p.AgentCode;
      this.md.WithholdingTax = p.WithholdingTax;
      this.md.TaxID = p.TaxID;
      this.md.Tel = p.Tel;
      this.md.Email = p.Email;
      this.md.Address = p.Address;
      this.md.Subdistrict = p.Subdistrict;
      this.md.City = p.City;
      this.md.Province = p.Province;
      this.md.Postcode = p.Postcode;
      this.md.UpperID = p.UpperID;
      this.md.UserTypeID = p.UserTypeID;
      this.md.ComprofileID = p.ComprofileID;
      this.md.PersonIsActive = p.IsActive;
      this.md.BranchName = p.BranchName;
      // Numeric branch_id comes from the UserList row (T1) — authoritative,
      // round-tripped so super-admin saves never zero it out.
      this.md.BranchID = row.BranchID;

      // Photo preview (fetch base64 only when the person has one).
      if (row.Photo && row.Photo != "") {
        // reuse the list-row cache when present
        if (this.avatarCache[row.PersonID]) {
          this.image = this.avatarCache[row.PersonID];
        } else {
          this.getImage(row.PersonID)
            .then(resp => {
              this.image = resp.data;
            })
            .catch(() => {
              this.image = "";
            });
        }
      }

      this.showEditModal = true;
    },
    onPhotoChange(file) {
      // CalmAvatarUpload emits the raw File (or null on clear).
      this.md.PhotoFile = file || null;
      if (file) this.md.RemovePhoto = false;
    },
    onPhotoRemove() {
      // Clear the local preview/selection and flag the stored photo for removal;
      // onUpdatePersonFromUser sends removePhoto=1 so the backend clears it.
      this.image = "";
      this.md.PhotoFile = null;
      this.md.RemovePhoto = true;
    },
    onUpdatePersonFromUser() {
      // Client-side required guard (name) — vee-validate also enforces this.
      if (!this.md.FName || this.md.FName.trim() == "") {
        swal.fire({
          title: this.$t("errors.invalid_parameter"),
          text: this.$t("fname"),
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false
        });
        return;
      }

      // birthday must round-trip as a valid date sentinel, like PersonTables.
      let birthDay = this.md.BirthDay == "" ? "0000-00-00" : this.md.BirthDay;

      // FULL-RECORD round-trip — every column PersonUpdateV2 overwrites is
      // re-sent so untouched fields are preserved (no corruption).
      let formData = new FormData();
      formData.append("ID", this.md.PersonID); // Person/updateData reads .get("ID")
      formData.append("branchID", this.md.BranchID); // authoritative numeric id
      formData.append("comprofileID", this.md.ComprofileID);
      formData.append("typeID", this.md.UserTypeID); // role read-only, but resent
      formData.append("fname", this.md.FName);
      formData.append("lname", this.md.LName);
      formData.append("birthDay", birthDay);
      formData.append("agentCode", this.md.AgentCode);
      formData.append("withholdingTax", this.md.WithholdingTax);
      formData.append("taxID", this.md.TaxID);
      formData.append("tel", this.md.Tel);
      formData.append("email", this.md.Email);
      formData.append("address", this.md.Address);
      formData.append("subdistrict", this.md.Subdistrict);
      formData.append("city", this.md.City);
      formData.append("province", this.md.Province);
      formData.append("postcode", this.md.Postcode);
      formData.append("uppderPersonID", this.md.UpperID == "" ? 0 : this.md.UpperID);
      formData.append("isActive", this.md.PersonIsActive);
      if (this.md.PhotoFile) {
        formData.append("photo", this.md.PhotoFile);
      } else if (this.md.RemovePhoto) {
        // Explicit removal -> backend clears the stored photo.
        formData.append("removePhoto", "1");
      }

      this.updatePerson(formData)
        .then(resp => {
          this.showEditModal = false;
          // invalidate this person's cached avatar so the new one is re-fetched
          this.$delete(this.avatarCache, this.md.PersonID);
          this.listData().then(() => {
            this.loadRowAvatars();
          });
          this.listPersonFull();
          swal.fire({
            title: this.$t("edit_success"),
            text: ``,
            icon: "success",
            confirmButtonClass: "btn btn-success btn-fill",
            buttonsStyling: false
          });
        })
        .catch(err => {
          swal.fire({
            title: this.$t(err.response.data.Msg),
            text: this.getErrorText(err.response.data),
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false
          });
        });
    },
    onUpdateUser() {
      this.createData({
        username: this.md.Username,
        password: this.md.Password,
        userType: this.md.Type,
        personID: this.md.PersonID
      })
        .then(resp => {
          this.clearModal(true);
          this.listData();
          swal.fire({
            title: this.$t("create_success"),
            text: ``,
            icon: "success",
            confirmButtonClass: "btn btn-success btn-fill",
            buttonsStyling: false
          });
        })
        .catch(err => {
          swal.fire({
            title: this.$t(err.response.data.Msg),
            text: this.getErrorText(err.response.data),
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false
          });
        });
    },
    onUpdatePassword() {
      this.changePasswordByAdmin({
        ID: this.md.ID,
        password: this.md.Password,
        confirm_password: this.md.Password
      })
        .then(resp => {
          this.clearModal(true);
          this.showAddModal = false;
          this.listData();
          swal.fire({
            title: this.$t("edit_success"),
            text: ``,
            icon: "success",
            confirmButtonClass: "btn btn-success btn-fill",
            buttonsStyling: false
          });
        })
        .catch(err => {
          swal.fire({
            title: this.$t(err.response.data.Msg),
            text: this.getErrorText(err.response.data),
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false
          });
        });
    },
    onUpdateStatus() {
      this.setActive({
        ID: this.md.ID,
        isActive: this.md.IsActive
      })
        .then(resp => {
          this.showAddModal = false;
          this.clearModal(true);
          this.listData();
          swal.fire({
            title: this.$t("edit_success"),
            text: ``,
            icon: "success",
            confirmButtonClass: "btn btn-success btn-fill",
            buttonsStyling: false
          });
        })
        .catch(err => {
          swal.fire({
            title: this.$t(err.response.data.Msg),
            text: this.getErrorText(err.response.data),
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false
          });
        });
    },
    handleChangePassword(row) {
      this.md.Form = 2;
      this.showAddModal = true;
      this.clearModal(true);
      this.md.ID = row.ID;
      this.md.Username = row.UserName;
    },
    handleSetActive(row) {
      this.md.Form = 3;
      this.showAddModal = true;
      this.md.ID = row.ID;
      this.md.IsActive = row.IsActive;
      this.md.Username = row.UserName;
    },
    selectionChange(selectedRows) {
      this.selectedRows = selectedRows;
    }
  }
};
</script>
<style scoped>
/* User administration — Calm skin. Namespaced under .usr so nothing leaks to
   other pages. The el-table is kept verbatim (§11 "skin, don't rewrite") and
   re-styled only via deep selectors below. */

/* Row-action cell: keep the text buttons inline + right-aligned. */
.usr__rowact {
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* Avatar cell in the list. */
.usr__avatar-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Modal form: stacked inputs + action row spacing. */
.usr__form >>> .form-group {
  margin-bottom: 16px;
}
.usr__form .calm__modal__actions {
  margin-top: 8px;
}
/* el-select inside the modal stretches full-width like the base inputs. */
.usr__form >>> .el-select {
  width: 100%;
}

/* Edit modal: large avatar centred at the top of the body. */
.usr__edit-avatar {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}
/* calm-form-row owns the spacing in the edit grid. */
.usr__edit >>> .calm__form-row .form-group {
  margin-bottom: 0;
}
.usr__edit >>> .calm__form-row .form-group > label {
  display: none;
}
.usr__actions {
  margin-top: 20px;
}

/* The free-text search box reuses the Kit's .ed input look. */
.usr >>> .ed {
  width: 100%;
}

/* Skin the existing <el-table> so it reads as a Calm surface without touching
   any column definitions or slots (§11). Deep selectors only. */
.usr >>> .el-table {
  color: var(--c-text, #2c3e50);
  background: transparent;
}
.usr >>> .el-table th,
.usr >>> .el-table .thead-light th {
  background: var(--c-surface-soft, #f4f7fb);
  color: var(--c-text-soft, #5b6b7e);
  font-weight: 600;
  border-bottom: 1px solid var(--c-border, #e3e9f0);
}
.usr >>> .el-table td {
  border-bottom: 1px solid var(--c-border, #eef2f7);
}
.usr >>> .el-table::before {
  background: var(--c-border, #e3e9f0);
}
.usr >>> .el-table__row:hover > td {
  background: var(--c-surface-soft, #f7fafd) !important;
}
</style>
